<script setup lang="ts">
import { computed, type Component } from 'vue'

interface Props {
  variant?: 'primary' | 'secondary' | 'outline' | 'danger' | 'ghost'
  type?: 'button' | 'submit' | 'reset'
  size?: 'sm' | 'md' | 'lg'
  disabled?: boolean
  ariaLabel?: string
  ariaDescribedBy?: string
  loading?: boolean
  icon?: Component
  iconPosition?: 'left' | 'right'
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  type: 'button',
  size: 'md',
  disabled: false,
  loading: false,
  icon: undefined,
  iconPosition: 'left'
})

const variantClasses: Record<string, string> = {
  primary: 'inline-flex items-center justify-center rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 dark:bg-primary-500 dark:hover:bg-primary-600',
  secondary: 'inline-flex items-center justify-center rounded-md bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600',
  outline: 'inline-flex items-center justify-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-600 dark:hover:bg-gray-700',
  danger: 'inline-flex items-center justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 dark:bg-red-700 dark:hover:bg-red-600',
  ghost: 'inline-flex items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 dark:text-gray-300 dark:hover:bg-gray-800',
}

const sizeClasses: Record<string, string> = {
  sm: '!px-2 !py-1 !text-xs !min-h-0',
  md: 'min-h-[44px]',
  lg: '!px-4 !py-2.5 !text-base min-h-[44px]'
}

const isDisabled = computed(() => props.disabled || props.loading)
</script>

<template>
  <button
    :type="type"
    :disabled="isDisabled"
    :aria-label="ariaLabel"
    :aria-describedby="ariaDescribedBy"
    :aria-busy="loading"
    :class="[
      variantClasses[variant] || variantClasses.primary,
      sizeClasses[size],
      isDisabled && 'opacity-50 cursor-not-allowed',
      'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-gray-900'
    ]"
  >
    <span
      v-if="loading"
      class="sr-only"
      aria-live="polite"
    >
      {{ $t('common.loading') }}
    </span>
    <component
      :is="icon"
      v-if="icon && iconPosition === 'left'"
      class="me-2 h-4 w-4 shrink-0"
      aria-hidden="true"
    />
    <slot />
    <component
      :is="icon"
      v-if="icon && iconPosition === 'right'"
      class="ms-2 h-4 w-4 shrink-0"
      aria-hidden="true"
    />
  </button>
</template>
